import { CommonModule } from '@angular/common';
import { Component, DestroyRef, EventEmitter, Input, OnInit, Output, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { MaterialModule } from 'src/app/material.module';
import { BaseRespone } from 'src/app/models/common.response';
import { ABHA_ADDRESS_MAX_LENGTH, abhaAddressValidator, withoutAbhaHandle } from '../abha-address.util';
import { AbhaService } from '../abha.service';
import { AbhaAddressResult, AbhaAddressSuggestionResult } from '../iabha-info';

// Lets the patient pick a suggested ABHA address or type their own for a new ABHA
// (NHA M1 CRT_ABHA_112).
@Component({
  selector: 'app-abha-address-select',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MaterialModule, TranslateModule],
  templateUrl: './abha-address-select.component.html',
  styleUrl: './abha-address-select.component.scss',
})
export class AbhaAddressSelectComponent implements OnInit {
  @Input({ required: true }) txnId!: string;
  @Input() currentAddress = '';
  @Output() readonly selected = new EventEmitter<string>();
  @Output() readonly kept = new EventEmitter<void>();

  readonly maxLength = ABHA_ADDRESS_MAX_LENGTH;
  readonly addressControl = new FormControl('', {
    nonNullable: true,
    validators: [Validators.required, abhaAddressValidator],
  });
  suggestions: readonly string[] = [];
  isLoading = false;
  isSaving = false;
  suggestionsError = '';
  private readonly destroyRef = inject(DestroyRef);

  constructor(
    private abhaService: AbhaService,
    private toastr: ToastrService,
    private translate: TranslateService,
  ) { }

  ngOnInit(): void {
    this.loadSuggestions();
  }

  loadSuggestions(): void {
    if (this.isLoading) {
      return;
    }
    this.suggestionsError = '';
    this.isLoading = true;
    this.abhaService
      .getAddressSuggestions(this.txnId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((response) => this.handleSuggestions(response));
  }

  onPickSuggestion(suggestion: string): void {
    this.addressControl.setValue(suggestion);
  }

  onSetAddress(): void {
    if (this.addressControl.invalid) {
      this.addressControl.markAsTouched();
      return;
    }

    this.isSaving = true;
    this.abhaService
      .setAddress({ txnId: this.txnId, abhaAddress: this.addressControl.value.trim() })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((response) => this.handleAddressSet(response));
  }

  private handleSuggestions(response: BaseRespone<AbhaAddressSuggestionResult>): void {
    this.isLoading = false;
    if (response && response.isSuccess && response.result) {
      this.suggestions = response.result.suggestions.map(withoutAbhaHandle);
      return;
    }
    this.suggestionsError = this.resolveError(response, 'ABHA.ADDRESS_SUGGESTIONS_FAILED');
    this.toastr.error(this.suggestionsError);
  }

  private handleAddressSet(response: BaseRespone<AbhaAddressResult>): void {
    this.isSaving = false;
    if (response && response.isSuccess && response.result?.abhaAddress) {
      this.toastr.success(this.translate.instant('ABHA.ADDRESS_SET_SUCCESS'));
      this.selected.emit(response.result.abhaAddress);
      return;
    }

    this.toastr.error(this.resolveError(response));
  }

  private resolveError(response: BaseRespone<unknown> | null, fallbackKey = 'ABHA.ADDRESS_SET_FAILED'): string {
    if (response && response.errors && response.errors.length > 0) {
      return response.errors.join('\n');
    }

    return response?.message || this.translate.instant(fallbackKey);
  }
}
